import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteField,
  doc,
  DocumentData,
  endAt,
  FieldValue,
  getDoc,
  getDocs,
  increment,
  limit,
  orderBy,
  query,
  setDoc,
  startAfter,
  startAt,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  listAll,
  ref,
  uploadBytes,
} from "firebase/storage";
import {
  distanceBetween,
  geohashForLocation,
  geohashQueryBounds,
} from "geofire-common";
import { toast } from "react-toastify";
import { string } from "yup";

import {
  NEARBY_CHEF_DISTANCE_LIMIT,
  NUMBER_OF_NEARBY_CHEF_LIMIT,
  POTENTIAL_FALSE_POSITIVES,
} from "../constants/General";
import ProposalState from "../constants/ProposalState";
import { VerifiedStatus } from "../constants/UserConstants";
import UserType from "../constants/UserType";
import getIsoStringFromTimestamp from "../helper/getIsoStringFromTimestamp";
import getModifiedNameWithUuid from "../helper/getModifiedNameWithUuid";
import { isItDev } from "../helper/isItDev";
import { ImageData } from "../interfaces/General";
import { UserProposedGig } from "../interfaces/Gig";
import { GoogleMapResultData } from "../interfaces/GoogleMap";
import User, { Settings } from "../interfaces/User";
import { PaginationData } from "../types/types";
import getZipCode from "../utils/get-zip-code";
import { sendEmailRequest, sendEmailRequestToAdmin } from "./email";
import { db, storage } from "./firebase";
import { createSystemLog, createUserLog } from "./logs";

const canViewGigForUser = (
  gig: Partial<UserProposedGig>,
  viewerUid?: string | null
) => {
  if (!gig?.isPrivate) {
    return true;
  }
  if (!viewerUid) {
    return false;
  }
  if (gig.ownerUid && gig.ownerUid === viewerUid) {
    return true;
  }
  return (gig.invitedChefUids ?? []).includes(viewerUid);
};

const addSettingsData = async (uid: string) => {
  try {
    const userRef = doc(db, "users", uid);
    const userData = (await getDoc(userRef)) as User;
    const basicSettings = await fetchBasicSettings();
    await setDoc(doc(db, "settings", uid), {
      ...basicSettings,
      service_charge: userData.userType === UserType.Chef ? 0.1 : 0.05,
      tax_amount: userData.userType === UserType.Chef ? 0 : 0,
    });
    return basicSettings;
  } catch (error: any) {
    isItDev() && toast.error("Something went wrong");
    await createUserLog(
      uid,
      "Error",
      "Error while adding settings",
      {
        message: error?.message || "",
        stack: error?.stack || "",
        name: error?.name || "",
      },
      "Settings Error"
    );
  }
};

const fetchUserSettings = async (uid: string): Promise<Settings> => {
  let result;

  try {
    // const docRef = doc(db, "settings", uid);
    // const docSnap = await getDoc(docRef);
    const userRef = doc(db, "users", uid);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data() as User;
    // if (docSnap.exists()) {
    // result = {
    //   service_charge: docSnap.data().service_charge,
    //   deposit_percentage: docSnap.data().deposit_percentage,
    // } as Settings;
    result = {
      service_charge: userData.userType === UserType.Chef ? 0.1 : 0.05,
      // deposit_percentage: docSnap.data().deposit_percentage,
      deposit_percentage: 0.25,
      tax_amount: 0.029,
    } as Settings;
    // } else {
    //   toast.error("Your settings are not available, please Contact Admin");
    // }
  } catch (error: any) {
    isItDev() && toast.error("Something Went Wrong");
    await createUserLog(
      uid,
      "Error",
      "Error while fetching settings",
      {
        message: error?.message || "",
        stack: error?.stack || "",
        name: error?.name || "",
      },
      "Settings Error"
    );
  }

  return result as Settings;
};

const fetchBasicSettings = async (): Promise<Settings> => {
  let result;

  try {
    const docRef = doc(db, "basicsettings", "_");
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      result = docSnap.data() as Settings;
    }
  } catch (error) {
    isItDev() && toast.error("Something Went Wrong");
    await createSystemLog("Error", "Error while fetching basic settings");
  }

  return result as Settings;
};

const fetchUserData = async (uid: string): Promise<User | null> => {
  let result;
  try {
    try {
      console.error("[fetchUserData] start", { uid });
    } catch (_) {}
    const docRef = doc(db, "users", uid);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      result = docSnap.data();
      result.uid = uid;

      // Handle date conversion with null checks
      if (result.dob && typeof result.dob.toDate === "function") {
        result.dob = result.dob.toDate().toISOString();
      }

      if (result.createdAt) {
        result.createdAt = getIsoStringFromTimestamp(result.createdAt);
      }

      if (result.insuranceExpiryDate) {
        result.insuranceExpiryDate = getIsoStringFromTimestamp(
          new Timestamp(
            result.insuranceExpiryDate.seconds,
            result.insuranceExpiryDate.nanoseconds
          )
        );
      }

      result.stripe_verification_attempted_at =
        result.stripe_verification_attempted_at &&
        typeof result.stripe_verification_attempted_at.toDate === "function"
          ? result.stripe_verification_attempted_at.toDate().toISOString()
          : null;
    } else {
      console.warn(`User document not found for uid: ${uid}`);
      return null;
    }
  } catch (error: any) {
    console.error(`Error fetching user data for uid: ${uid}`, error);
    await createUserLog(
      uid,
      "Error",
      "Error while fetching user data",
      {
        message: error?.message || "",
        stack: error?.stack || "",
        name: error?.name || "",
      },
      "User Data Error"
    );
    return null;
  }
  try {
    console.error("[fetchUserData] done", {
      uid,
      found: Boolean(result),
    });
  } catch (_) {}
  return result as User;
};

const fetchUidFromVanity = async (vanity: string) => {
  let result;
  try {
    const q = query(collection(db, "users"), where("vanity", "==", vanity));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      result = querySnapshot.docs[0].id;
    } else {
      console.log("User not found for vanity:", vanity);
    }
  } catch (error) {
    console.log("Error while fetching user for vanity:", vanity, error);
    await createSystemLog(
      "Error",
      "Error while fetching user for vanity: " + vanity
    );
  }
  return result;
};

const formatNameForUrl = (name: string): string => {
  return name.toLowerCase().replace(/\s+/g, "-");
};

// Helper function to create the dynamic URL string
const createChefDynamicUrl = (
  firstName: string,
  lastName: string,
  index: number // 0 for the first, 1 for the second, etc.
): string => {
  const formattedFirstName = formatNameForUrl(firstName);
  const formattedLastName = formatNameForUrl(lastName);
  const base = `${formattedFirstName}-${formattedLastName}`;
  // Append 1-based index for duplicates (index > 0)
  return index > 0 ? `${base}-${index}` : base;
};

// --- Function to get all Chef Dynamic URLs ---
// Fetches all chefs and generates their unique dynamic URLs for getStaticPaths
async function fetchAllChefDynamicUrls(): Promise<string[]> {
  console.log(
    "Fetching all chef dynamic URLs for getStaticPaths and ensuring lowercase fields..."
  );
  const chefUrls: string[] = [];
  const nameCounts: { [key: string]: number } = {};

  try {
    // 1. Query all chefs
    const chefsQuery = query(
      collection(db, "users"), // Using client 'db' for reads initially is fine
      where("userType", "==", "chef")
      // No specific order needed here yet, as we'll process and then sort later if necessary for URL generation
      // However, ordering by createdAt can be good practice if you want a consistent processing order.
    );

    const snapshot = await getDocs(chefsQuery);

    if (snapshot.empty) {
      console.log("No chef users found to process.");
      return [];
    }

    // 2. Process each chef: check/update lowercase fields & prepare for URL generation
    // For batching Firestore writes to be more efficient
    let batch = writeBatch(db); // Use client 'db' for batching if admin SDK is tricky here, or use admin.firestore().batch()
    let operationsInBatch = 0;
    const MAX_OPERATIONS_PER_BATCH = 490; // Firestore batch limit is 500

    const processedChefsData = []; // To store data needed for URL generation after potential updates

    for (const userDoc of snapshot.docs) {
      const data = userDoc.data();
      const docId = userDoc.id;
      let needsUpdate = false;
      const updates: { [key: string]: string } = {};

      const currentFirstName = data.firstName as string | undefined;
      const currentLastName = data.lastName as string | undefined;

      let lowerFirstName = data.lowerFirstName as string | undefined;
      let lowerLastName = data.lowerLastName as string | undefined;

      // Check and prepare update for lowerFirstName
      if (currentFirstName) {
        const expectedLowerFirstName = currentFirstName.toLowerCase();
        if (lowerFirstName !== expectedLowerFirstName) {
          updates.lowerFirstName = expectedLowerFirstName;
          lowerFirstName = expectedLowerFirstName; // Use the new value for URL generation
          needsUpdate = true;
        }
      } else if (lowerFirstName !== undefined) {
        // If firstName is gone but lowerFirstName exists, consider removing it or setting to null
        // For simplicity here, we'll assume firstName always exists for chefs.
        // If not, you might need admin.firestore.FieldValue.delete() via admin SDK.
      }

      // Check and prepare update for lowerLastName
      if (currentLastName) {
        const expectedLowerLastName = currentLastName.toLowerCase();
        if (lowerLastName !== expectedLowerLastName) {
          updates.lowerLastName = expectedLowerLastName;
          lowerLastName = expectedLowerLastName; // Use the new value for URL generation
          needsUpdate = true;
        }
      } else if (lowerLastName !== undefined) {
        // Similar to firstName
      }

      if (needsUpdate) {
        console.log(
          `User ${docId}: Queuing update for lowercase names:`,
          updates
        );
        const userDocRef = doc(db, "users", docId); // Get DocumentReference
        batch.update(userDocRef, updates);
        operationsInBatch++;

        if (operationsInBatch >= MAX_OPERATIONS_PER_BATCH) {
          console.log(
            `Committing batch of ${operationsInBatch} Firestore updates...`
          );
          await batch.commit();
          batch = writeBatch(db); // Start a new batch
          operationsInBatch = 0;
        }
      }

      // Store the potentially updated (or existing) lowercase names and createdAt for URL generation
      // Ensure lowerFirstName and lowerLastName are defined for URL generation
      if (lowerFirstName && lowerLastName && data.createdAt) {
        processedChefsData.push({
          id: docId,
          lowerFirstName,
          lowerLastName,
          createdAt: data.createdAt, // Keep for sorting duplicates
        });
      } else {
        // Handle chefs with missing data - create a fallback URL using their UID
        console.warn(
          `Chef document ${docId} is missing critical data (firstName, lastName, or createdAt). Creating fallback URL.`
        );
        // Create a fallback URL using the UID for chefs with missing data
        const fallbackUrl = `chef-${docId}`;
        chefUrls.push(fallbackUrl);
      }
    }

    // Commit any remaining operations in the last batch
    if (operationsInBatch > 0) {
      console.log(
        `Committing final batch of ${operationsInBatch} Firestore updates...`
      );
      await batch.commit();
    }
    console.log("Lowercase field checks and updates complete.");

    // 3. Sort processed chefs data for consistent URL generation (especially for duplicates)
    // This sort MUST match the logic in `fetchUidFromChefName` if it queries and sorts.
    processedChefsData.sort((a, b) => {
      if (a.lowerFirstName < b.lowerFirstName) return -1;
      if (a.lowerFirstName > b.lowerFirstName) return 1;
      if (a.lowerLastName < b.lowerLastName) return -1;
      if (a.lowerLastName > b.lowerLastName) return 1;
      // Finally, sort by createdAt if names are identical
      const timeA = a.createdAt?.toMillis?.() || 0;
      const timeB = b.createdAt?.toMillis?.() || 0;
      return timeA - timeB;
    });

    // 4. Generate Dynamic URLs from the processed (and now sorted) data
    console.log("Generating dynamic URLs...");
    processedChefsData.forEach((chefData) => {
      // These are now guaranteed to be the correct lowercase versions
      const { lowerFirstName, lowerLastName } = chefData;

      if (!lowerFirstName || !lowerLastName) {
        // This check should ideally be redundant if the previous filtering was correct
        console.warn(
          `Chef document ${chefData.id} missing lowerFirstName or lowerLastName during URL generation phase. Skipping.`
        );
        return;
      }

      const nameKey = `${lowerFirstName}-${lowerLastName}`;
      const currentCount = nameCounts[nameKey] || 0;
      nameCounts[nameKey] = currentCount + 1;

      const dynamicUrl = createChefDynamicUrl(
        lowerFirstName,
        lowerLastName,
        currentCount // 0-indexed count for duplicates
      );
      chefUrls.push(dynamicUrl);
    });

    console.log(`Successfully generated ${chefUrls.length} chef URLs.`);
    return chefUrls;
  } catch (error) {
    console.error("Error in fetchAllChefDynamicUrls:", error);
    // Depending on build requirements, you might want to throw or return empty
    return []; // Return empty array on error to avoid build failure
  }
}

function parseChefDynamicUrl(url: string) {
  const parts = url.split("-");
  let firstName,
    lastName,
    number = null;

  if (parts.length === 3) {
    [firstName, lastName, number] = parts;
  } else if (parts.length === 2) {
    [firstName, lastName] = parts;
  } else {
    throw new Error("Invalid URL format");
  }

  return {
    firstName: firstName.toLowerCase(),
    lastName: lastName.toLowerCase(),
    number,
  };
}

const fetchUidFromChefName = async (chefDynamicUrl: string) => {
  let result;

  // Handle fallback URLs (chef-{uid} format)
  // TODO: try to improve this logic
  if (chefDynamicUrl.startsWith("chef-")) {
    const uid = chefDynamicUrl.replace("chef-", "");
    // Verify this is actually a chef
    try {
      const userDoc = await getDoc(doc(db, "users", uid));
      if (userDoc.exists() && userDoc.data().userType === "chef") {
        return uid;
      }
    } catch (error) {
      console.error(
        `Error verifying fallback URL for ${chefDynamicUrl}:`,
        error
      );
    }
    return null;
  }

  const { firstName, lastName, number } = parseChefDynamicUrl(chefDynamicUrl); // These are already lowercase

  try {
    const q = query(
      collection(db, "users"),
      where("lowerFirstName", "==", firstName), // Query 'lowerFirstName'
      where("lowerLastName", "==", lastName), // Query 'lowerLastName'
      where("userType", "==", "chef"),
      orderBy("createdAt", "asc") // Crucial for consistent ordering for duplicates
    );

    const snapshot = await getDocs(q);
    const sortedDocs = snapshot.docs; // Already sorted by createdAt by the query

    const index = number ? parseInt(number) : 0;
    const doc = sortedDocs[index];

    if (doc && doc.id) {
      result = doc.id;
    } else {
      console.warn(
        `User not found for chefDynamicUrl: ${chefDynamicUrl} (parsed: ${firstName}-${lastName}-${number}, index: ${index})`
      );
    }
  } catch (error) {
    console.error(
      `Error while fetching user for chefDynamicUrl: ${chefDynamicUrl}`,
      error
    );
    // await createSystemLog(...);
  }
  return result;
};

const fetchGigIdFromGigTitle = async (vanity: string) => {
  // For the page gig/gig-title
  let result;
  try {
    const q = query(collection(db, "users"), where("vanity", "==", vanity));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      result = querySnapshot.docs[0].id;
    } else {
      console.log("User not found for vanity:", vanity);
    }
  } catch (error) {
    console.log("Error while fetching user for vanity:", vanity, error);
    await createSystemLog(
      "Error",
      "Error while fetching user for vanity: " + vanity
    );
  }
  return result;
};

const fetchUidFromPublicName = async (publicName: string) => {
  let result;
  try {
    const q = query(
      collection(db, "users"),
      where("publicName", "==", publicName)
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      result = querySnapshot.docs[0].id;
    } else {
      return false;
    }
  } catch (error) {
    console.log("Error while fetching user for publicName:", publicName, error);
    return null;
  }
  return result;
};

const fetchVanitySuggestions = async (vanity: string) => {
  let result: string[] = [];
  try {
    const q = query(
      collection(db, "users"),
      where("vanity", ">=", vanity),
      where("vanity", "<=", vanity + "\uf8ff")
    );
    const querySnapshot = await getDocs(q);
    let existingVanity = [];
    if (!querySnapshot.empty) {
      existingVanity = querySnapshot.docs.map((doc) => doc.data().vanity);
    }
    let i = 1;
    while (result.length < 3) {
      if (!existingVanity.includes(vanity + "-" + i)) {
        console.log(vanity + "-" + i);
        result.push(vanity + "-" + i);
      }
      i++;
    }
  } catch (error) {
    console.log("Error while fetching suggestions for vanity:", vanity, error);
    await createSystemLog(
      "Error",
      "Error while fetching suggestions for vanity: " + vanity
    );
  }
  return result;
};

const fetchAvailablePublicName = async (
  firstName: string,
  lastName: string
) => {
  let result;
  try {
    const publicName = firstName + lastName;
    const q = query(
      collection(db, "users"),
      where("publicName", ">=", publicName),
      where("publicName", "<=", publicName + "\uf8ff")
    );
    const querySnapshot = await getDocs(q);
    let existingPublicNames = [];
    if (!querySnapshot.empty) {
      existingPublicNames = querySnapshot.docs.map(
        (doc) => doc.data().publicName
      );
    }
    let i = 1;
    while (result === undefined) {
      const newPublicName = publicName + " " + i;
      if (!existingPublicNames.includes(newPublicName)) {
        result = newPublicName;
      }
      i++;
    }
  } catch (error) {
    console.log("Error while fetching available public name:", error);
    await createSystemLog(
      "Error",
      "Error while fetching available public name for user: " +
        firstName +
        " " +
        lastName
    );
    return "";
  }
  return result;
};

const updateSingleUserData = async (
  userUid: string,
  field: keyof User,
  value: number | string | Timestamp | string[] | GoogleMapResultData | boolean |FieldValue,
  toastData?: string
) => {
  const docRef = doc(db, "users", userUid);

  await updateDoc(docRef, {
    [field]: value,
  });

  // Log important profile updates
  const importantFields: (keyof User)[] = [
    "address",
    "firstName",
    "lastName",
    "bio",
    "tagline",
    "verified",
    "phoneNumber",
    "email",
  ];

  if (importantFields.includes(field)) {
    await createUserLog(
      userUid,
      "Profile Updated",
      `Profile field '${field}' was updated`,
      { field, hasValue: !!value },
      "Profile Field Update"
    );
  }

  toastData && toast.success(toastData);
};

const uploadImage = async (imageFile: File, userUid: string) => {
  const modifiedFile = getModifiedNameWithUuid(imageFile);
  const storageRef = ref(
    storage,
    `users/${userUid}/images/${modifiedFile.name}`
  );
  const snapshot = await uploadBytes(storageRef, modifiedFile);
  const url = await getDownloadURL(snapshot.ref);
  incrementImagesCount(userUid);
  return { url, imageRef: snapshot.ref.fullPath };
};

const uploadGigImage = async (imageFile: File, userUid: string) => {
  const modifiedFile = getModifiedNameWithUuid(imageFile);
  const storageRef = ref(
    storage,
    `users/${userUid}/gigImages/${modifiedFile.name}`
  );
  const snapshot = await uploadBytes(storageRef, modifiedFile);
  const url = await getDownloadURL(snapshot.ref);
  return { url, imageRef: snapshot.ref.fullPath };
};

const uploadProfileImage = async (imageFile: File, userUid: string) => {
  console.log("🖼️ uploadProfileImage called for user:", userUid);
  try {
    const storageRef = ref(storage, `users/${userUid}/profileImage`);
    const snapshot = await uploadBytes(storageRef, imageFile);
    const url = await getDownloadURL(snapshot.ref);
    console.log("✅ Image uploaded, URL:", url);
    console.log("📝 Updating user data...");
    await updateSingleUserData(userUid, "profileImage", url);
    console.log("📝 User data updated, now creating log...");
    await createUserLog(
      userUid,
      "Profile Image Uploaded",
      "User uploaded a new profile image",
      { imageSize: imageFile.size, imageType: imageFile.type },
      "Profile Image Upload"
    );
    console.log("✅ uploadProfileImage completed successfully");
    return url;
  } catch (error) {
    console.error("❌ ERROR in uploadProfileImage:", error);
    throw error;
  }
};
const uploadBannerImage = async (imageFile: File, userUid: string) => {
  const storageRef = ref(storage, `users/${userUid}/bannerImage`);
  const snapshot = await uploadBytes(storageRef, imageFile);
  const url = await getDownloadURL(snapshot.ref);
  await updateSingleUserData(userUid, "bannerImage", url);
  await createUserLog(
    userUid,
    "Banner Image Uploaded",
    "User uploaded a new banner image",
    { imageSize: imageFile.size, imageType: imageFile.type },
    "Banner Image Upload"
  );
  return url;
};

const setBannerImage = async (url: string, userUid: string) => {
  updateSingleUserData(userUid, "bannerImage", url);
};

const setProfileImageUrl = async (url: string, userUid: string) => {
  updateSingleUserData(userUid, "profileImage", url);
};

const removeProfileImage = async (userUid: string) => {
  const storageRef = ref(storage, `users/${userUid}/profileImage`);
  try {
    await deleteObject(storageRef);
    await createUserLog(
      userUid,
      "Profile Image Removed",
      "User removed their profile image",
      {},
      "Profile Image Removal"
    );
  } catch (error) {
    console.error("Error removing profile image:", error);
    // Still try to log the removal attempt
    try {
      await createUserLog(
        userUid,
        "Profile Image Removed",
        "User attempted to remove their profile image",
        { error: error instanceof Error ? error.message : "Unknown error" },
        "Profile Image Removal"
      );
    } catch (logError) {
      console.error("Error creating log for profile image removal:", logError);
    }
  }
};

const fetchImagesFromUid = async (userUid: string | string[]) => {
  const listRef = ref(storage, `users/${userUid}/images`);
  const res = await listAll(listRef);
  const imageList = await Promise.all(
    res.items.map(async (imageRef) => {
      const url = await getDownloadURL(imageRef);
      return { url, imageRef: imageRef.fullPath };
    })
  );
  return imageList;
};

const fetchUserProfileImage = async (userUid: string | string[]) => {
  try {
    const storageRef = ref(storage, `users/${userUid}/profileImage`);
    const url = await getDownloadURL(storageRef);
    return url;
  } catch (error) {
    console.log(error);
    await createSystemLog(
      "Error",
      "Error while fetching user profile image for user: " + userUid
    );
    return null;
  }
};

const fetchGigs = async () => {
  const q = query(
    collection(db, "gigs"),
    where("gigState", "in", [ProposalState.NEW_GIG, ProposalState.OPEN_BID])
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) => {
    const data = doc.data();
    data.gigId = doc.id;
    data.createdAt = getIsoStringFromTimestamp(data.createdAt);
    data.finishedAndPaidAt = getIsoStringFromTimestamp(data.finishedAndPaidAt);
    data.depositPaidAt = getIsoStringFromTimestamp(data.depositPaidAt);
    return data;
  });
};

const fetchUsers = async () => {
  const querySnapshot = await getDocs(collection(db, "users"));
  return querySnapshot.docs.map((doc) => {
    const data = doc.data();
    data.uid = doc.id;
    data.dob = data.dob.toDate().toISOString();
    data.createdAt = data.createdAt.toDate().toISOString();
    data.stripe_verification_attempted_at =
      data.stripe_verification_attempted_at
        ? data.stripe_verification_attempted_at.toDate().toISOString()
        : null;
    return data;
  });
};

const imageRemoveFromDB = async (userData: User, imageRef: string) => {
  const imageRefObj = ref(storage, imageRef);
  try {
    const url = await getDownloadURL(imageRefObj);
    await deleteObject(imageRefObj);
    if (
      imageRefObj.parent?.name === "images" &&
      imageRefObj.parent?.parent?.parent?.name === "users"
    ) {
      await decrementImagesCount(imageRefObj.parent?.parent?.name);
      await checkImageRefUsage(userData, url);
    }
  } catch (error) {
    console.log(error);
  }
};

const checkImageRefUsage = async (userData: User, url: string) => {
  const userRef = doc(db, `users/${userData.uid}`);
  const promisesArray = [];
  switch (url) {
    case userData.profileImage:
      promisesArray.push(
        await updateDoc(userRef, {
          ["profileImage"]: deleteField(),
        })
      );
    case userData.bannerImage:
      promisesArray.push(
        await updateDoc(userRef, {
          ["bannerImage"]: deleteField(),
        })
      );
  }
  await Promise.all(promisesArray);
};

const incrementImagesCount = async (userUid: string) => {
  const docRef = doc(db, "users", userUid);
  await updateDoc(docRef, {
    imagesCount: increment(1),
  });
};
const decrementImagesCount = async (userUid: string) => {
  const docRef = doc(db, "users", userUid);
  await updateDoc(docRef, {
    imagesCount: increment(-1),
  });
};

const removeMultipleImagesFromStorage = async (imagesData: ImageData[]) => {
  await Promise.all(
    imagesData.map(async (imageData) => {
      const imageRefObj = ref(storage, imageData.imageRef);
      await deleteObject(imageRefObj).catch((error) => {
        console.log(error);
      });
    })
  );
};

const fetchSingleGig = async (uid: string): Promise<UserProposedGig> => {
  let result;
  try {
    try {
      console.error("[fetchSingleGig] start", { gigId: uid });
    } catch (_) {}
    const docRef = doc(db, "gigs", uid);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      result = docSnap.data();

      result.createdAt =
        result.createdAt && getIsoStringFromTimestamp(result.createdAt);
      if (result.finishedAndPaidAt) {
        result.finishedAndPaidAt = getIsoStringFromTimestamp(
          result.finishedAndPaidAt
        );
      }
      if (result.depositPaidAt) {
        result.depositPaidAt = getIsoStringFromTimestamp(result.depositPaidAt);
      }
      result.gigId = docSnap.id;
      try {
        console.error("[fetchSingleGig] done", {
          gigId: uid,
          exists: true,
          ownerUid: result?.ownerUid,
          state: result?.gigState,
          isPrivate: Boolean(result?.isPrivate),
        });
      } catch (_) {}
    } else {
      try {
        console.error("[fetchSingleGig] notFound", { gigId: uid });
      } catch (_) {}
    }
  } catch (error) {
    console.error("[fetchSingleGig] error", error);
  }
  return result as UserProposedGig;
};

const uploadImageWithPath = async (imageFile: File, path: string) => {
  const modifiedFile = getModifiedNameWithUuid(imageFile);
  const storageRef = ref(storage, `${path}/${modifiedFile.name}`);
  const snapshot = await uploadBytes(storageRef, modifiedFile);
  const url = await getDownloadURL(snapshot.ref);
  return { url, imageRef: snapshot.ref.fullPath };
};

const getUserTypeUsingUid = async (userUid: string) => {
  let result;
  try {
    const docRef = doc(db, "users", userUid);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      result = docSnap.data().userType;
    }
  } catch (error) {
    console.log(error);
  }
  return result;
};

const addFavouriteGig = async (userUid: string, gigId: string) => {
  try {
    const docRef = doc(db, "gigs", gigId);
    await updateDoc(docRef, {
      likedBy: arrayUnion(userUid),
    });
    await createUserLog(
      userUid,
      "Gig Favorited",
      `User favorited gig ${gigId}`,
      { gigId },
      "Gig Favorite"
    );
  } catch (error) {
    console.log(error);
  }
};

const addFavouriteProposal = async (userUid: string, proposalId: string) => {
  try {
    const docRef = doc(db, "proposals", proposalId);
    await updateDoc(docRef, {
      likedGigs: arrayUnion(userUid),
    });
  } catch (error) {
    console.log(error);
  }
};

const removeFavouriteGig = async (userUid: string, gigId: string) => {
  try {
    const docRef = doc(db, "gigs", gigId);
    await updateDoc(docRef, {
      likedBy: arrayRemove(userUid),
    });
    await createUserLog(
      userUid,
      "Gig Unfavorited",
      `User unfavorited gig ${gigId}`,
      { gigId },
      "Gig Unfavorite"
    );
  } catch (error) {
    console.log(error);
  }
};

const fetchlikedGigs = async (userId: string): Promise<UserProposedGig[]> => {
  const docsRef = collection(db, "gigs");
  const q = query(docsRef, where("likedBy", "array-contains", userId));
  const docs = await getDocs(q);
  const likedGigs = (
    await Promise.all(
      docs.docs.map(async (doc) => {
        const result = doc.data();

        result.createdAt =
          result.createdAt && getIsoStringFromTimestamp(result.createdAt);
        result.finishedAndPaidAt =
          result.finishedAndPaidAt &&
          getIsoStringFromTimestamp(result.finishedAndPaidAt);
        result.depositPaidAt =
          result.depositPaidAt &&
          getIsoStringFromTimestamp(result.depositPaidAt);
        result.gigId = doc.id;
        result.invitedChefUids = result.invitedChefUids ?? [];
        result.privateInviteLimit = result.privateInviteLimit ?? 0;

        return result;
      })
    )
  ).filter((gig) => canViewGigForUser(gig, userId)) as UserProposedGig[];
  return likedGigs;
};

const checkProposalSubmitted = async (userUid: string, gigId: string) => {
  const docsRef = collection(db, "proposals");
  const q = query(
    docsRef,
    where("chefUid", "==", userUid),
    where("gigId", "==", gigId),
    where("proposalState", "not-in", [ProposalState.DRAFT])
  );
  const docs = await getDocs(q);
  let didChefSubmitProposal = false;
  if (docs.docs.length > 0) {
    didChefSubmitProposal = true;
  }
  return didChefSubmitProposal;
};

const getChefs = async (
  paginationData: PaginationData,
  lastDocId: string | null,
  userUid: string | null,
  filter?: "Hawaii" | "Maui" | "Oahu" | "Kauai"
) => {
  try {
    const { limit: limitNum } = paginationData;
    let chefsData: User[];
    const lastDocSnapshot =
      lastDocId && (await getDoc(doc(db, `users/${lastDocId}`)));
    const snapShot = await getDocs(
      query(
        collection(db, "users"),
        where("userType", "==", "chef"),
        where("verified", "==", "True"),

        ...(filter
          ? [where("address.postalCode", "in", getZipCode(filter))]
          : []),
        ...(userUid ? [where("likedBy", "array-contains", userUid)] : []),
        ...(lastDocId ? [startAfter(lastDocSnapshot)] : []),
        limit(limitNum)
      )
    );

    chefsData = await Promise.all(
      snapShot.docs.map(async (doc) => {
        const data = doc.data();
        data.uid = doc.id;
        data.createdAt = data.createdAt?.toDate().toISOString();
        data.updatedAt = data.updatedAt?.toDate().toISOString();
        return data as User;
      })
    );

    return chefsData;
  } catch (error) {
    console.log(error);
  }
};

const getUidsOfChefs = async () => {
  let uids: string[];
  const snapShot = await getDocs(
    query(
      collection(db, "users"),
      where("userType", "==", "chef")
      // Removed verified filter to include all chefs in sitemap
    )
  );

  uids = await Promise.all(
    snapShot.docs.map(async (doc) => {
      return doc.id;
    })
  );
  console.log(uids);

  return uids;
};

const updateGeoHash = async (path: string, lat: number, lng: number) => {
  const geoHash = geohashForLocation([lat, lng]);
  const docRef = doc(db, path);
  await updateDoc(docRef, {
    geoHash: geoHash,
  });

  // Log address/location update if it's a user document
  if (path.startsWith("users/")) {
    const userId = path.split("/")[1];
    await createUserLog(
      userId,
      "Address/Location Updated",
      "User updated their address/location",
      { lat, lng, geoHash },
      "Location Update"
    );
  }
};

const getNearByChefs = async (lat: number, lng: number) => {
  const radiusInM = NEARBY_CHEF_DISTANCE_LIMIT * 1000;
  const bounds = geohashQueryBounds([lat, lng], radiusInM);
  let snapShots: DocumentData[] = [];
  for (const bound of bounds) {
    const querySnapshot = await getDocs(
      query(
        collection(db, "users"),
        where("userType", "==", "chef"),
        where("verified", "==", "True"),
        orderBy("geoHash"),
        startAt(bound[0]),
        endAt(bound[1]),
        limit(NUMBER_OF_NEARBY_CHEF_LIMIT + POTENTIAL_FALSE_POSITIVES)
      )
    );
    snapShots = [...snapShots, ...querySnapshot.docs];
  }

  const filteredSnapShot: DocumentData[] = [];
  for (const doc of snapShots) {
    const address = doc.get("address") as GoogleMapResultData;
    const distanceInKm = distanceBetween(
      [address.lat, address.lng],
      [lat, lng]
    );
    if (distanceInKm * 1000 <= radiusInM) {
      filteredSnapShot.push(doc);
    }
  }

  return filteredSnapShot.slice(0, NUMBER_OF_NEARBY_CHEF_LIMIT).map((doc) => {
    const data = doc.data();
    data.uid = doc.id;
    data.dob = data.dob.toDate().toISOString();
    data.createdAt = data.createdAt.toDate().toISOString();
    data.stripe_verification_attempted_at =
      data.stripe_verification_attempted_at
        ? data.stripe_verification_attempted_at.toDate().toISOString()
        : null;
    return data;
  });
};

const addFavouriteChef = async (userUid: string, chefUid: string) => {
  try {
    const clientRef = doc(db, "users", userUid);
    await updateDoc(clientRef, {
      likedChefs: arrayUnion(chefUid),
    });
    const chefRef = doc(db, "users", chefUid);
    await updateDoc(chefRef, {
      likedBy: arrayUnion(userUid),
    });
    await createUserLog(
      userUid,
      "Chef Favorited",
      `User favorited chef ${chefUid}`,
      { chefUid },
      "Chef Favorite"
    );
  } catch (error) {
    console.log(error);
  }
};

const removeFavouriteChef = async (userUid: string, chefUid: string) => {
  try {
    const clientRef = doc(db, "users", userUid);
    await updateDoc(clientRef, {
      likedChefs: arrayRemove(chefUid),
    });
    const chefRef = doc(db, "users", chefUid);
    await updateDoc(chefRef, {
      likedBy: arrayRemove(userUid),
    });
    await createUserLog(
      userUid,
      "Chef Unfavorited",
      `User unfavorited chef ${chefUid}`,
      { chefUid },
      "Chef Unfavorite"
    );
  } catch (error) {
    console.log(error);
  }
};

const uploadFileWithFileName = async (file: File, path: string) => {
  const storageRef = ref(storage, path);
  const snapshot = await uploadBytes(storageRef, file);
  const url = await getDownloadURL(snapshot.ref);
  return url;
};

const applyForVerifiedStatus = async (userId: string) => {
  try {
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, {
      verified: VerifiedStatus.Applied,
    });

    const userData = (await getDoc(userRef)).data();

    await createUserLog(
      userId,
      "Verification Status Applied",
      "User applied for verified status",
      { previousStatus: userData?.verified, newStatus: VerifiedStatus.Applied },
      "Verification Application"
    );

    // Send OB6H email
    const userMetaRef = doc(db, "user_meta_data", userId);
    const userMetaDoc = await getDoc(userMetaRef);
    const userMeta = userMetaDoc.data();

    if (userMeta?.email_logs?.some((x: any) => x.mailId === "OB6H")) {
      return;
    }

    await sendEmailRequest(userId, "OB6H", [
      { key: "chefName", value: userData?.firstName || "Chef" },
    ]);

    // Update user meta data
    await updateDoc(userMetaRef, {
      email_logs: arrayUnion({
        mailId: "OB6H",
        createdAt: Timestamp.now(),
      }),
    });
  } catch (error) {
    console.log(error);
  }
};

const setNewChefStatus = async (userId: string) => {
  try {
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, {
      verified: VerifiedStatus.False,
    });
  } catch (error) {
    console.log(error);
  }
};

export {
  addFavouriteChef,
  addFavouriteGig,
  addFavouriteProposal,
  addSettingsData,
  applyForVerifiedStatus,
  checkProposalSubmitted,
  fetchAvailablePublicName,
  fetchGigs,
  fetchImagesFromUid,
  fetchlikedGigs,
  fetchSingleGig,
  fetchUidFromPublicName,
  fetchUidFromVanity,
  fetchUserData,
  fetchUserProfileImage,
  fetchUsers,
  fetchUserSettings,
  fetchVanitySuggestions,
  getChefs,
  getNearByChefs,
  getUidsOfChefs,
  getUserTypeUsingUid,
  imageRemoveFromDB,
  removeFavouriteChef,
  removeFavouriteGig,
  removeMultipleImagesFromStorage,
  removeProfileImage,
  setBannerImage,
  setNewChefStatus,
  setProfileImageUrl,
  updateGeoHash,
  updateSingleUserData,
  uploadBannerImage,
  uploadFileWithFileName,
  uploadGigImage,
  uploadImage,
  uploadImageWithPath,
  uploadProfileImage,
  fetchUidFromChefName,
  fetchGigIdFromGigTitle,
  fetchAllChefDynamicUrls,
};
